import RenderMarkdown from '@components/RTEditor/RenderMarkdown';
import {
  ListItem,
  ListItemProps,
  makeStyles,
  Paper,
  SvgIcon,
  Typography,
} from '@material-ui/core';
import {
  FilesIcon,
  FlowIcon,
  LocationIcon,
} from '@src/components/AdminHome/QuickMessages/SvgIcons';
import { QuickMessageType } from '@src/components/AdminHome/QuickMessages/types';
import { QuickMessage } from '@types';
import { useCallback } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';

const HighlightText: React.FC<{ mark: string; label: string }> = ({
  label,
  mark,
}) => {
  const start = label.toLocaleLowerCase().indexOf(mark.toLocaleLowerCase());
  if (start === -1) return <span>{label}</span>;
  const end = start + mark.length;

  return (
    <span>
      {label.slice(0, start)}
      <b>{label.slice(start, end)}</b>
      {label.slice(end)}
    </span>
  );
};

type QuickMessageListItemProps = Omit<ListItemProps<'li'>, 'onClick'> & {
  message: QuickMessage;
  command: string;
  onClick(message: QuickMessage): void;
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& .MuiTypography-body1': {
      marginBottom: 4,
    },
    '& .MuiPaper-root': {
      alignSelf: 'stretch',
      padding: 4,
    },
  },
  svgIcon: {
    color: theme.palette.text.primary,
  },
}));

const QuickMessageListItem = injectIntl<
  'intl',
  QuickMessageListItemProps & WrappedComponentProps
>(({ message, command, onClick, intl: { formatMessage }, ...restProps }) => {
  const classes = useStyles();

  const onItemClick = useCallback(() => onClick(message), [message, onClick]);

  return (
    <ListItem
      className={classes.root}
      onClick={onItemClick}
      component="li"
      {...restProps}
      button
    >
      <Typography variant="body1">
        <HighlightText label={message.command} mark={command} />
      </Typography>
      <Paper variant="outlined">
        <Typography variant="body2" component="div">
          {message.type === QuickMessageType.LOCATION && (
            <div style={{ display: 'flex', alignItems: 'self-end' }}>
              <SvgIcon className={classes.svgIcon}>{LocationIcon}</SvgIcon>
              <b>
                {formatMessage({ id: 'QuickMessages.location.address' })}
              </b>: {message.location?.address}
            </div>
          )}
          {message.type === QuickMessageType.FLOW && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SvgIcon className={classes.svgIcon}>{FlowIcon}</SvgIcon>{' '}
              {message.flow_data?.message.body.text}
            </div>
          )}
          {message.type === QuickMessageType.TEXT && (
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            <RenderMarkdown markdown={message.message!} />
          )}
          {message.type === QuickMessageType.FILES && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SvgIcon className={classes.svgIcon}>{FilesIcon}</SvgIcon>
              {message.message && <RenderMarkdown markdown={message.message} />}
            </div>
          )}
        </Typography>
      </Paper>
    </ListItem>
  );
});

export default QuickMessageListItem;
